import { useEffect, useRef, useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import createGlobe from 'cobe';

interface PulseMarker {
  id: string;
  location: [number, number];
  delay: number;
  name: string;
  state: string;
  arenas: number;
}

const markers: PulseMarker[] = [
  { id: 'bh', location: [-19.92, -43.94], delay: 0, name: 'Belo Horizonte', state: 'MG', arenas: 11 },
  { id: 'sl', location: [-19.46, -44.25], delay: 0.3, name: 'Sete Lagoas', state: 'MG', arenas: 2 },
  { id: 'pe', location: [-21.09, -45.09], delay: 0.6, name: 'Perdões', state: 'MG', arenas: 1 },
  { id: 'it', location: [-17.86, -42.86], delay: 0.9, name: 'Itamarandiba', state: 'MG', arenas: 1 },
  { id: 'di', location: [-18.25, -43.60], delay: 1.2, name: 'Diamantina', state: 'MG', arenas: 1 },
  { id: 'je', location: [-13.85, -40.08], delay: 1.5, name: 'Jequié', state: 'BA', arenas: 1 },
  { id: 'pi', location: [-13.50, -39.50], delay: 1.8, name: 'Piaúma', state: 'BA', arenas: 1 },
  { id: 'ma', location: [-3.12, -60.02], delay: 2.1, name: 'Manaus', state: 'AM', arenas: 1 },
];

function GlobeBrazil() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointerInteracting = useRef<{ x: number; y: number } | null>(null);
  const dragOffset = useRef({ phi: 0, theta: 0 });
  const phiOffsetRef = useRef(0);
  const thetaOffsetRef = useRef(0);
  const isPausedRef = useRef(false);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    pointerInteracting.current = { x: e.clientX, y: e.clientY };
    if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
    isPausedRef.current = true;
  }, []);

  const handlePointerUp = useCallback(() => {
    if (pointerInteracting.current !== null) {
      phiOffsetRef.current += dragOffset.current.phi;
      thetaOffsetRef.current += dragOffset.current.theta;
      dragOffset.current = { phi: 0, theta: 0 };
    }
    pointerInteracting.current = null;
    if (canvasRef.current) canvasRef.current.style.cursor = 'grab';
    isPausedRef.current = false;
  }, []);

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (pointerInteracting.current !== null) {
        dragOffset.current = {
          phi: (e.clientX - pointerInteracting.current.x) / 300,
          theta: (e.clientY - pointerInteracting.current.y) / 1000,
        };
      }
    };
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerup', handlePointerUp, { passive: true });
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [handlePointerUp]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    let globe: any = null;
    let animationId: number;
    let phi = 0.78;
    let destroyed = false;

    function init() {
      if (destroyed || globe) return;
      const parent = canvas.parentElement;
      const width = parent ? parent.clientWidth : canvas.offsetWidth;
      if (width === 0) return;

      // Set canvas dimensions explicitly
      canvas.width = width * (window.devicePixelRatio || 1);
      canvas.height = width * (window.devicePixelRatio || 1);
      canvas.style.width = width + 'px';
      canvas.style.height = width + 'px';

      globe = createGlobe(canvas, {
        devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2),
        width: width * Math.min(window.devicePixelRatio || 1, 2),
        height: width * Math.min(window.devicePixelRatio || 1, 2),
        phi: 0.78,
        theta: 0.15,
        dark: 1,
        diffuse: 1.5,
        mapSamples: 16000,
        mapBrightness: 8,
        baseColor: [0.15, 0.15, 0.15],
        markerColor: [0.9, 0.47, 0.02],
        glowColor: [0.05, 0.05, 0.05],
        markers: markers.map((m) => ({ location: m.location, size: 0.06 })),
        opacity: 0.85,
      } as any);

      function animate() {
        if (destroyed) return;
        if (!isPausedRef.current) phi += 0.002;
        if (globe) {
          globe.update({
            phi: phi + phiOffsetRef.current + dragOffset.current.phi,
            theta: 0.15 + thetaOffsetRef.current + dragOffset.current.theta,
          });
        }
        animationId = requestAnimationFrame(animate);
      }
      animate();
      setTimeout(() => { if (canvas) canvas.style.opacity = '1'; }, 100);
    }

    // Try init immediately, retry after short delay if width is 0
    const tryInit = () => {
      if (canvas.parentElement && canvas.parentElement.clientWidth > 0) {
        init();
      } else {
        setTimeout(tryInit, 200);
      }
    };
    tryInit();

    return () => {
      destroyed = true;
      if (animationId) cancelAnimationFrame(animationId);
      if (globe) globe.destroy();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={handlePointerDown}
      style={{
        width: '100%',
        height: '100%',
        cursor: 'grab',
        opacity: 0,
        transition: 'opacity 1.2s ease',
        touchAction: 'none',
      }}
    />
  );
}

export default function BrazilMap() {
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);

  return (
    <section className="py-16 md:py-24 px-4" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Globe */}
          <div className="relative flex justify-center">
            <div style={{ width: '100%', maxWidth: '450px', aspectRatio: '1/1' }}>
              <GlobeBrazil />
            </div>
          </div>

          {/* Right: Info */}
          <div>
            <span className="inline-block text-orange-400 text-sm font-bold uppercase tracking-widest mb-4">
              Presença Nacional
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
              Clientes que confiam no nosso trabalho, em todo o Brasil
            </h2>
            <p className="text-zinc-500 mb-8 leading-relaxed">
              De Manaus a Jequié, nossas câmeras gravam em arenas por todo o país.
              Tecnologia Apertai presente onde o esporte acontece.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 text-center">
                <p className="text-2xl md:text-3xl font-bold text-orange-400">∞</p>
                <p className="text-zinc-500 text-xs mt-1">Arenas confiam</p>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 text-center">
                <p className="text-2xl md:text-3xl font-bold text-orange-400">5</p>
                <p className="text-zinc-500 text-xs mt-1">Estados</p>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 text-center">
                <p className="text-2xl md:text-3xl font-bold text-orange-400">8</p>
                <p className="text-zinc-500 text-xs mt-1">Cidades</p>
              </div>
            </div>

            {/* City pills */}
            <div className="flex flex-wrap gap-2">
              {markers.map((loc) => (
                <motion.div
                  key={loc.id}
                  whileHover={{ scale: 1.05 }}
                  onHoverStart={() => setHoveredCity(loc.name)}
                  onHoverEnd={() => setHoveredCity(null)}
                  className="flex items-center gap-2 bg-zinc-900/40 border border-zinc-800 rounded-full px-3 py-1.5 cursor-default transition-colors"
                  style={hoveredCity === loc.name ? { borderColor: '#e5760550', backgroundColor: '#e5760510' } : {}}
                >
                  <div className="w-2 h-2 rounded-full bg-orange-500" style={{ boxShadow: '0 0 6px rgba(229,118,5,0.5)' }} />
                  <span className="text-zinc-300 text-xs font-medium">{loc.name}</span>
                  <span className="text-zinc-600 text-xs">{loc.state}</span>
                  {loc.arenas > 1 && (
                    <span className="text-orange-400 text-xs font-bold">{loc.arenas}</span>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
